import { useEffect, useRef } from 'react';
import { useSoundContext } from '@/providers/SoundContext';

/**
 * Custom hook to automatically register audio/video elements with the global volume controller.
 *
 * Usage:
 * ```tsx
 * const videoRef = useGlobalVolume<HTMLVideoElement>('my-video-id');
 *
 * return <video ref={videoRef} src="..." />;
 * ```
 *
 * The element's volume will automatically be controlled by the global volume setting.
 * When soundEnabled is false, the element will be muted (volume = 0).
 * When soundEnabled is true, the element will use the global volume setting.
 * The element will be unregistered on unmount.
 */
export function useGlobalVolume<T extends HTMLAudioElement | HTMLVideoElement>(elementId: string) {
  const { registerAudioElement, unregisterAudioElement, globalVolume, soundEnabled } = useSoundContext();
  const elementRef = useRef<T>(null);

  useEffect(() => {
    const element = elementRef.current;

    if (element) {
      // Register the element with the global volume controller
      registerAudioElement(elementId, element);

      // Cleanup: unregister on unmount
      return () => {
        unregisterAudioElement(elementId);
      };
    }
  }, [elementId, registerAudioElement, unregisterAudioElement]);

  // Handle mute/unmute based on soundEnabled
  useEffect(() => {
    const element = elementRef.current;

    if (element) {
      if (soundEnabled) {
        // Unmuted: use global volume
        element.volume = globalVolume;
        element.muted = false;
      } else {
        // Muted: set volume to 0 and mute
        element.volume = 0;
        element.muted = true;
      }
    }
  }, [soundEnabled, globalVolume]);

  return elementRef;
}
